// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import loadMessages from "@insite/client-framework/Store/Data/Messages/Handlers/LoadMessages";
import translate from "@insite/client-framework/Translate";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Typography from "@store/MobiusOverrides/Typography/Typography";
import { countBubblePositionStyles, countBubbleStyles } from "@store/Themes/common/mixins/countBubble";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { useSession, useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useMessagesDataView } from "@toolkit/Framework/Store/Data/Messages/MessagesHook";
import React, { FC, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

const messagesParameter = {};

export interface AccountMessagesCountPresentationProps extends ExpandStyles<AccountMessagesCountStyles> {
    position?: (string | null)[];
}

export interface AccountMessagesCountProps extends AccountMessagesCountPresentationProps {
    /* Store Custom Code - Start */
    /**
     * When enabled, will force show this component, even when the global extension feature flag is turned off.
     */
    forceShow?: boolean;
    /* Store Custom Code - End */
}

export interface AccountMessagesCountStyles {
    messagesCountText?: TypographyPresentationProps;
}

export const accountMessagesCountStyles: AccountMessagesCountStyles = {
    messagesCountText: {
        css: css`
            ${countBubbleStyles()}
        `,
    },
};

const AccountMessagesCount: FC<AccountMessagesCountProps> = ({
    extendedStyles,
    overridedStyles,
    position,
    // Store Custom Code
    forceShow,
}) => {
    const styles = useExpandProps(accountMessagesCountStyles, extendedStyles, overridedStyles);
    const baseMessagesDataView = useMessagesDataView(messagesParameter);
    const { value: messages, isLoading } = usePersistentDataView(baseMessagesDataView);
    // Starter Removed Code - const shouldLoadMessages = !isLoading && !messages; (moved below; now also gated on showAccountMessagesNotificationCount)
    const { extensionWebsiteSettings } = useSettingsCollection();
    const showAccountMessagesNotificationCount =
        forceShow || // Store Custom Code
        (extensionWebsiteSettings && extensionWebsiteSettings.showAccountMessagesNotificationCount);
    const shouldLoadMessages = showAccountMessagesNotificationCount && !isLoading && !messages; // Store Custom Code
    const dispatch = useAppDispatch();
    const [messagesCount, setMessagesCount] = useState(0);
    const session = useSession();
    const isAuthenticated = session && session.isAuthenticated && !session.isGuest;

    useEffect(() => {
        if (shouldLoadMessages && isAuthenticated) {
            dispatch(loadMessages(messagesParameter));
        }
    }, []);

    useEffect(() => {
        const _count = messages?.filter(msg => !msg.isRead).length || 0;
        setMessagesCount(_count);
    }, [messages]);

    const messagesCountStyles = useMemo(() => {
        if (!position) {
            return styles.messagesCountText;
        }
        return mergeProps<AccountMessagesCountStyles["messagesCountText"]>(styles.messagesCountText, {
            css: css`
                ${countBubblePositionStyles(position)}
            `,
        });
    }, [position, styles.messagesCountText]);

    if (!showAccountMessagesNotificationCount || !messagesCount) {
        return null;
    }

    return (
        <Typography {...messagesCountStyles} data-test-selector="messageCount" /* Store Custom Code */>
            {messagesCount}
            <VisuallyHidden>{translate("unread messages")}</VisuallyHidden>
        </Typography>
    );
};

export default AccountMessagesCount;
